import { getGradedResponses, formatGradedResponses, } from './rematching';

import { checkFillInTheBlankQuestion } from '../../../Shared/quill-marking-logic/src/libs/graders/fill_in_the_blank';
import { extractBlankValues } from '../extractBlankValues';
import convertConceptResultsArrayToHash from '../convertConceptResultsArrayToHash';
import { hashToCollection, maxAttemptsForGrammarFillInBlank, numberOfBlanksForFillInBlankPrompt, } from '../../../Shared/index';

interface Response {
  id: string;
  text: string;
  count?: number;
  question_uid?: string;
  author?: string;
  feedback?: string;
  optimal?: boolean;
  parent_id?: string | null;
  concept_results?: any;
  [key: string]: any;
}

interface Question {
  blankByBlankGrading?: any;
  caseInsensitive?: boolean;
  conceptID?: string;
  modelConceptUID?: string;
  concept_uid?: string;
  prompt: string;
  [key: string]: any;
}


/**
 * Rematch a single fill-in-blank response using the fill-in-blank grading logic
 */
export function rematchFillInBlankResponse(
  response: Response,
  question: Question,
  questionID: string,
  callback: Function
) {
  // Get all graded responses first (needed for matching logic)
  getGradedResponses(questionID).then((gradedResponses: any) => {
    const filteredGradedResponses = gradedResponses.filter(gr => [true, false].includes(gr.optimal) && gr.text !== response.text)
    const formattedGradedResponses = hashToCollection(formatGradedResponses(filteredGradedResponses))
    // Extract blank values from the response text
    const inputVals = extractBlankValues(response.text, question.prompt);

    // Get default concept UID
    const defaultConceptUID = question.modelConceptUID || question.conceptID || question.concept_uid;

    // Grade the response using fill-in-blank grading with all graded responses
    const gradedResponse = checkFillInTheBlankQuestion(
      questionID,
      response.text,
      formattedGradedResponses,
      question.caseInsensitive || false,
      defaultConceptUID,
      false, // isDiagnosticFIB
      question.blankByBlankGrading || {},
      inputVals,
      maxAttemptsForGrammarFillInBlank(question.cues.length, numberOfBlanksForFillInBlankPrompt(question.prompt), question.blankAllowed)
    );

    // Determine what to do based on the graded response
    const hasMatch = gradedResponse.author || gradedResponse.feedback;

    if (hasMatch) {
      // Update the response with the new grading
      const conceptResults = gradedResponse.conceptResults || gradedResponse.concept_results;

      const newVals = {
        weak: false,
        parent_id: gradedResponse.parent_id || null,
        author: gradedResponse.author,
        feedback: gradedResponse.feedback || '',
        concept_results: convertConceptResultsArrayToHash(conceptResults),
        optimal: gradedResponse.optimal
      };

      return updateResponse(response.id, newVals).then(() => {
        callback && callback();
      });
    } else {
      // Unmatch the response
      const newVals = {
        weak: false,
        feedback: null,
        parent_id: null,
        author: null,
        optimal: null,
        text: response.text,
        count: response.count,
        question_uid: response.question_uid,
      };

      return updateResponse(response.id, newVals).then(() => {
        callback && callback();
      });
    }
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error('Error rematching fill-in-blank response:', error);
    callback && callback();
  });
}

function updateResponse(responseID: string, content: any) {
  return fetch(`${window.postbuildEnv.QUILL_CMS}/responses/${responseID}`, {
    method: 'PUT',
    mode: 'cors',
    credentials: 'include',
    body: JSON.stringify({ response: content }),
    headers: { 'Content-Type': 'application/json' },
  }).then((response) => {
    if (!response.ok) {
      throw Error(response.statusText);
    }
    return response.json();
  });
}
